import { z } from 'zod';
import { BaseModel } from '../base.model';
import { ReportIds } from '../../contracts/reporting-api/enums/report';

export const USER_SETTINGS_COLLECTION = 'user-settings';
export const USER_SETTINGS_REPORTING_DOC_ID = 'reporting';
export const USER_SETTINGS_REPORTS_DOC_ID = 'reports';
export const USER_SETTINGS_PUBLIC_REPORTS_DOC_ID = 'public-reports';

/**
 * Firestore paths:
 * - private: user-settings/reporting/{report_id}/reports
 * - public:  user-settings/reporting/{report_id}/public-reports
 *
 * Collection: user-settings/reporting/{report_id}
 * Document id: reports | public-reports
 */
export const getUserSettingsReportsCollectionPath = (reportId: ReportIds | string): string =>
  [USER_SETTINGS_COLLECTION, USER_SETTINGS_REPORTING_DOC_ID, reportId].join('/');

export const getUserSettingsReportsDocumentId = (isPublic = false): string =>
  isPublic ? USER_SETTINGS_PUBLIC_REPORTS_DOC_ID : USER_SETTINGS_REPORTS_DOC_ID;

export enum ReportDefaultSettingsKeys {
  reportId = 'report_id',
  period = 'period',
  startDate = 'start_date',
  endDate = 'end_date',
  title = 'title',
  subtitle = 'subtitle',
  rowsPerPage = 'rows_per_page',
  displayThreshold = 'display_threshold',
  includeThreshold = 'include_threshold',
  ntpDisplayMode = 'ntp_display_mode',
  showSuppressedAgents = 'show_suppressed_agents',
  showTotals = 'show_totals',
  /** @deprecated Read only to migrate settings saved before `ntp_display_mode` was introduced. */
  showNtpSeparately = 'show_ntp_separately',
  /** @deprecated Read only to migrate settings saved before `show_suppressed_agents` was introduced. */
  hideSuppressedAgents = 'hide_suppressed_agents',
  isPublic = 'is_public',
}

export const reportDefaultSettingsDocumentSchema = z.object({
  [ReportDefaultSettingsKeys.reportId]: z.nativeEnum(ReportIds),
  [ReportDefaultSettingsKeys.period]: z.enum(['ytd', 'weekly', 'monthly', 'quarterly', 'custom']).optional(),
  [ReportDefaultSettingsKeys.startDate]: z.string(),
  [ReportDefaultSettingsKeys.endDate]: z.string(),
  [ReportDefaultSettingsKeys.title]: z.string(),
  [ReportDefaultSettingsKeys.subtitle]: z.string(),
  [ReportDefaultSettingsKeys.rowsPerPage]: z.coerce.number().int().positive(),
  [ReportDefaultSettingsKeys.displayThreshold]: z.coerce.number().nonnegative(),
  /** Missing on settings saved before the switch existed, which means the threshold applies. */
  [ReportDefaultSettingsKeys.includeThreshold]: z.boolean().optional(),
  [ReportDefaultSettingsKeys.ntpDisplayMode]: z.enum(['hide', 'separate', 'general_list']).optional(),
  [ReportDefaultSettingsKeys.showSuppressedAgents]: z.boolean().optional(),
  [ReportDefaultSettingsKeys.showTotals]: z.boolean().optional(),
  /** Legacy persisted values are accepted so existing users retain their saved preference. */
  [ReportDefaultSettingsKeys.showNtpSeparately]: z.boolean().optional(),
  [ReportDefaultSettingsKeys.hideSuppressedAgents]: z.boolean().optional(),
  [ReportDefaultSettingsKeys.isPublic]: z.boolean(),
});

export type ReportDefaultSettingsDocumentDto = z.infer<typeof reportDefaultSettingsDocumentSchema>;

export const reportDefaultSettingsUpsertSchema = reportDefaultSettingsDocumentSchema.omit({
  [ReportDefaultSettingsKeys.reportId]: true,
});

export type ReportDefaultSettingsUpsertDto = z.infer<typeof reportDefaultSettingsUpsertSchema>;

export class ReportDefaultSettings extends BaseModel {
  [ReportDefaultSettingsKeys.reportId]: ReportIds;
  [ReportDefaultSettingsKeys.period]?: 'ytd' | 'weekly' | 'monthly' | 'quarterly' | 'custom';
  [ReportDefaultSettingsKeys.startDate]: string;
  [ReportDefaultSettingsKeys.endDate]: string;
  [ReportDefaultSettingsKeys.title]: string;
  [ReportDefaultSettingsKeys.subtitle]: string;
  [ReportDefaultSettingsKeys.rowsPerPage]: number;
  [ReportDefaultSettingsKeys.displayThreshold]: number;
  [ReportDefaultSettingsKeys.includeThreshold]?: boolean;
  [ReportDefaultSettingsKeys.ntpDisplayMode]?: 'hide' | 'separate' | 'general_list';
  [ReportDefaultSettingsKeys.showSuppressedAgents]?: boolean;
  [ReportDefaultSettingsKeys.showTotals]?: boolean;
  /** @deprecated Read only for settings saved before `ntp_display_mode` was introduced. */
  [ReportDefaultSettingsKeys.showNtpSeparately]?: boolean;
  /** @deprecated Read only for settings saved before `show_suppressed_agents` was introduced. */
  [ReportDefaultSettingsKeys.hideSuppressedAgents]?: boolean;
  [ReportDefaultSettingsKeys.isPublic]: boolean;
}
